/**
* Реальный API-клиент для AI-чата.
 *
 * Архитектура (после перехода на WebSocket):
 *  - все REST-вызовы идут на amportal (Laravel)
 *  - `streamMessage()` теперь делает короткий POST /api/chat/stream (отдаёт
 *    request_id + user_message_id за миллисекунды) и подписывается на
 *    private-канал `chat.thread.{externalId}` через Laravel Echo / Reverb.
 *  - Чанки приходят как событие `chat.chunk` и проходят через тот же
 *    `emitChunks()`, что и раньше для SSE — слой useChatStore не меняется.
 *  - Поэтому reload страницы во время ответа больше не теряет хвост: при
 *    повторной подписке Reverb прокачивает накопленные сообщения, а
 *    StreamChatMessageJob держит соединение с FastAPI независимо от вкладки.
 */
import { getEcho } from '@/utils/echo'
import { ecommerceAdd } from '@/utils/analytics'


const BASE_URL = process.env.VUE_APP_URL_DEV || 'http://localhost:8080'

const SESSION_KEY = 'chat_session_token'

// Сколько ждём ПЕРВЫЙ реальный чанк из WS-канала после принятого POST /stream.
// Бэкенд обычно начинает стрим за 1-3с; 30с — щедрый запас на холодный старт
// LLM/очереди, после которого считаем, что подписка не работает.
const WS_FIRST_CHUNK_MS = 30000

function getSessionToken() {
    if (typeof localStorage === 'undefined') return ''
    return localStorage.getItem(SESSION_KEY) || ''
}

function storeSessionToken(token) {
    if (!token || typeof localStorage === 'undefined') return
    localStorage.setItem(SESSION_KEY, token)
}

/**
 * Гарантирует, что `chat_session_token` уже выдан сервером ДО первой подписки
 * на private-канал. Без этого Echo-authorizer уходит на broadcasting/auth с
 * пустым токеном, middleware ChatGuestOrAuth генерирует новый UUID, и токен
 * WS-канала расходится с тем, под которым REST создаёт тред → колбэк
 * chat.thread.* отвечает 403, а пользователь бесконечно ждёт стрим.
 *
 * Для авторизованного пользователя session_token не нужен (доступ по user_id),
 * поэтому ранний выход. Запрос дедуплицируется через in-flight промис.
 */
let sessionBootstrapPromise = null
export async function ensureSessionToken() {
    if (typeof localStorage === 'undefined') return ''
    const existing = getSessionToken()
    if (existing) return existing
    if (localStorage.getItem('token')) return ''
    if (!sessionBootstrapPromise) {
        // GET /chat/plan — самый дешёвый guest-доступный эндпоинт под
        // middleware chat.session: его ответ выдаёт X-Chat-Session-Token,
        // jsonFetch его и сохранит.
        sessionBootstrapPromise = jsonFetch(`${BASE_URL}/api/chat/plan`)
            .catch(() => null)
            .finally(() => { sessionBootstrapPromise = null })
    }
    await sessionBootstrapPromise
    return getSessionToken()
}

function authHeaders(extra = {}) {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
    const sessionToken = getSessionToken()
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(sessionToken ? { 'X-Chat-Session-Token': sessionToken } : {}),
        ...extra,
    }
}

function isAuthed() {
    if (typeof localStorage === 'undefined') return false
    return Boolean(localStorage.getItem('token'))
}

/**
 * Публичные эндпоинты (тарифы, каталог источников/регионов, форматы ответа,
 * стартовые подсказки) отдаём и гостю, всё остальное режем на клиенте, чтобы
 * не слать заведомо-401 на бэк после того, как гостевой режим убран.
 */
const PUBLIC_CHAT_PATHS = [
    '/api/chat/plans',
    '/api/chat/regions/config-preview',
    '/api/chat/regions/catalog',
    '/api/chat/sources/config-preview',
    '/api/chat/response-format/catalog',
    '/api/chat/suggestions',
    '/api/chat/plan-payment-webhook',
    '/api/chat/addon-payment-webhook',
]

function isPublicChatUrl(url) {
    try {
        const path = new URL(url, BASE_URL).pathname
        // Все /api/chat/shares/{token}/* публичны только для конкретного токена
        // (это отдельная авторизация), сюда их не включаем — фронт использует
        // отдельные функции для публичных отчётов/share.
        return PUBLIC_CHAT_PATHS.some(p => path === p)
    } catch (e) {
        return false
    }
}

function guestBlockedError() {
    const err = new Error('Требуется вход в аккаунт')
    err.status = 401
    err.payload = { reason: 'auth_required' }
    return err
}

async function jsonFetch(url, options = {}) {
    // Единый guard от гостевых запросов к чат-API: если бэк-эндпоинт не в
    // публичном белом списке и токена нет — не идём в сеть, кидаем 401 сразу.
    // ChatPage/GuestOverlay уже показывает CTA на регистрацию, лишний трафик
    // и «мигания» состояния не нужны.
    if (url.startsWith(`${BASE_URL}/api/chat/`) && !isAuthed() && !isPublicChatUrl(url)) {
        throw guestBlockedError()
    }
    const response = await fetch(url, {
        ...options,
        headers: authHeaders(options.headers || {}),
    })
    const issued = response.headers.get('X-Chat-Session-Token')
    if (issued) storeSessionToken(issued)
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
        const error = new Error(data?.message || `Request failed: ${response.status}`)
        error.status = response.status
        error.payload = data
        throw error
    }
    return data
}

export function createStreamController() {
    if (typeof AbortController !== 'undefined') {
        return new AbortController()
    }
    return { signal: { aborted: false }, abort() { this.signal.aborted = true } }
}

/**
 * Открыть SSE-стрим. Парсит чанки и нормализует их к формату, который ожидает store.
 *
 * request_id выпускается сервером (Laravel) — фронт получает его из:
 *   1) HTTP-заголовка `X-Request-Id`
 *   2) первого SSE-чанка `event: meta { requestId }`
 * и прокидывает в onChunk({ type: 'meta', requestId }) — store сохраняет для cancel.
 */
export function dispatchServerChunk(payload, onChunk) {
    emitChunks(payload, onChunk)
}

/**
 * Кэш активных подписок: один канал на один тред живёт между запросами,
 * чтобы не дёргать broadcasting/auth на каждое сообщение. Ключ — externalId.
 * Значение — { channel, refCount }: канал отписывается, когда refCount=0.
 */
const channelCache = new Map()

/**
 * Подписаться на private-канал треда. Используется и из streamMessage()
 * (для текущего ответа), и из useChatStore при открытии треда — чтобы
 * получать broadcast'ы из второй вкладки или продолжение ответа после reload.
 *
 * Возвращает функцию-отписку.
 */
export function subscribeToThread(threadId, onChunk) {
    if (!threadId) return () => {}
    const echo = getEcho()
    const channelName = `chat.thread.${threadId}`

    let entry = channelCache.get(threadId)
    if (!entry) {
        const channel = echo.private(channelName)
        entry = { channel, listeners: new Set() }
        channelCache.set(threadId, entry)
        // `.chat.chunk` — точка перед именем означает «использовать broadcastAs()».
        // Echo сам подставит подходящий префикс, иначе ловит «App\\Events\\…».
        channel.listen('.chat.chunk', (payload) => {
            for (const fn of entry.listeners) {
                emitChunks(payload, fn)
            }
        })
    }
    entry.listeners.add(onChunk)

    return () => {
        entry.listeners.delete(onChunk)
        if (entry.listeners.size === 0) {
            try {
                echo.leave(channelName)
            } catch {
                // noop
            }
            channelCache.delete(threadId)
        }
    }
}

/**
 * Отправить сообщение в чат.
 *
 * Сейчас это уже не SSE-stream, а короткий POST: контроллер ставит
 * StreamChatMessageJob в очередь и сразу возвращает request_id + БД-id
 * пользовательского сообщения. Сами чанки ответа прилетают в WS-канал треда,
 * на который мы подписываемся до отправки POST'а (чтобы не пропустить
 * первый meta-чанк).
 *
 * Promise резолвится, когда пришёл финальный meta с assistantMessageId или
 * чанк type='done'. Если abort'нули через signal — резолвимся с aborted=true.
 */
export async function streamMessage({
    text,
    threadId,
    context,
    filterSnapshot,
    allowedSources,
    history,
    lastDataset,
    attachments,
    regenerateAssistantMessageId,
    variantFormatLabel,
    variantChartFormats,
    responseFormats,
    responseBlocks,
    signal,
    onChunk,
}) {
    let resolveDone
    let rejectDone
    const donePromise = new Promise((resolve, reject) => {
        resolveDone = resolve
        rejectDone = reject
    })

    let requestIdForCancel = null
    let assistantMessageIdSeen = null
    let finished = false

    function finish(result) {
        if (finished) return
        finished = true
        clearWatchdog()
        unsubscribe()
        if (signal && abortHandler) signal.removeEventListener('abort', abortHandler)
        resolveDone(result)
    }

    function fail(err) {
        if (finished) return
        finished = true
        clearWatchdog()
        unsubscribe()
        if (signal && abortHandler) signal.removeEventListener('abort', abortHandler)
        rejectDone(err)
    }

    // Гарантируем стабильный session_token ДО подписки: иначе broadcasting/auth
    // уйдёт с пустым токеном, сервер выдаст новый UUID, и канал получит 403.
    try {
        await ensureSessionToken()
    } catch {
        // noop — при сбое подписка всё равно попробует с тем, что есть.
    }

    // Watchdog: если подписка на канал провалилась (403/закрытый WS) — ни один
    // чанк не придёт, а POST /stream вернёт 202 и job отстримит «в пустоту».
    // Без таймера donePromise висел бы вечно («пользователь сидит ждёт»).
    // Любой реальный чанк сбрасывает watchdog: значит канал живой.
    let receivedAnyChunk = false
    let watchdog = null
    const clearWatchdog = () => {
        if (watchdog) {
            clearTimeout(watchdog)
            watchdog = null
        }
    }

    // Подписываемся ДО POST'а — иначе job может успеть запустить стрим и
    // первые чанки пройдут мимо. Reverb сам буферизует подписку в течение
    // handshake'а, но лучше перестраховаться.
    const unsubscribe = subscribeToThread(threadId, (chunk) => {
        receivedAnyChunk = true
        clearWatchdog()
        // Прокидываем чанк в store как раньше.
        onChunk?.(chunk)
        // Финализация: meta с assistantMessageId или явный done.
        if (chunk?.type === 'meta' && typeof chunk.assistantMessageId === 'number') {
            assistantMessageIdSeen = chunk.assistantMessageId
        }
        if (chunk?.type === 'done') {
            finish({ aborted: false, requestId: requestIdForCancel, assistantMessageId: assistantMessageIdSeen })
        }
    })

    const abortHandler = () => {
        // signal.aborted уже выставлен. Просим бэк отменить — он закроет
        // SSE-сессию FastAPI'и job завершится. Дублирующий done нам уже не
        // нужен — выходим сразу.
        if (requestIdForCancel) {
            cancelStream(requestIdForCancel).catch(() => {})
        }
        finish({ aborted: true, requestId: requestIdForCancel })
    }
    if (signal) {
        if (signal.aborted) {
            finish({ aborted: true, requestId: null })
            return donePromise
        }
        signal.addEventListener('abort', abortHandler)
    }

    let response
    try {
        response = await fetch(`${BASE_URL}/api/chat/stream`, {
            method: 'POST',
            headers: authHeaders({ Accept: 'application/json' }),
            body: JSON.stringify({
                thread_id: threadId,
                text,
                context: context || [],
                filter_snapshot: filterSnapshot || null,
                allowed_sources: allowedSources || [],
                history: history || [],
                last_dataset: lastDataset || null,
                attachments: attachments || [],
                regenerate_assistant_message_id: regenerateAssistantMessageId ?? undefined,
                // Шаблоны ответа для этого сообщения. Поле важнее сохранённого
                // дефолта пользователя, поэтому им же работает перегенерация.
                // response_blocks приходит от персонального шаблона промпта,
                // где блоки собраны поштучно, минуя пресеты.
                response_formats: Array.isArray(responseFormats) ? responseFormats : undefined,
                response_blocks: Array.isArray(responseBlocks) && responseBlocks.length
                    ? responseBlocks
                    : undefined,
                variant_format_label: variantFormatLabel ?? undefined,
                variant_chart_formats: Array.isArray(variantChartFormats) && variantChartFormats.length
                    ? variantChartFormats.map(f => ({
                        card_index: f.cardIndex,
                        chartable_index: f.chartableIndex,
                        chart_type: f.chartType,
                        title: f.title || '',
                    }))
                    : undefined,
            }),
        })
    } catch (err) {
        fail(err)
        return donePromise
    }

    const issuedSession = response.headers.get('X-Chat-Session-Token')
    if (issuedSession) storeSessionToken(issuedSession)

    if (!response.ok) {
        const payload = await response.json().catch(() => null)
        // Отказ по лимитам/доступу — не ошибка транспорта: отдаём store'у
        // limit-чанк, чтобы он показал причину человеческим текстом.
        if (payload?.type === 'limit') {
            onChunk?.(payload)
            finish({ aborted: false, requestId: null })
            return donePromise
        }
        const err = new Error(payload?.message || `Stream failed: ${response.status}`)
        err.status = response.status
        err.payload = payload
        fail(err)
        return donePromise
    }

    const acceptedPayload = await response.json().catch(() => ({}))
    requestIdForCancel = acceptedPayload?.request_id || null
    const userMessageId = acceptedPayload?.user_message_id || null

    // Сразу эмитим meta — чтобы store привязал serverId к локальному
    // user-сообщению, не дожидаясь WS-чанка (он тоже придёт, но повторное
    // присвоение того же id безопасно).
    if (onChunk && requestIdForCancel) {
        onChunk({ type: 'meta', requestId: requestIdForCancel, userMessageId })
    }

    // POST принят (202), job стримит чанки в WS-канал. Если за WS_FIRST_CHUNK_MS
    // не прилетело ни одного реального чанка — почти наверняка подписка не
    // авторизовалась (403 на broadcasting/auth) или WS не поднялся. Не висим
    // молча: проваливаем промис с понятной ошибкой, чтобы store показал её
    // пользователю, а не «вечную загрузку».
    if (!receivedAnyChunk && !finished) {
        watchdog = setTimeout(() => {
            if (receivedAnyChunk || finished) return
            const err = new Error(
                'Не удалось получить ответ по WebSocket: подписка на канал не подтверждена. '
                + 'Обновите страницу и попробуйте снова.',
            )
            err.code = 'ws_channel_timeout'
            err.requestId = requestIdForCancel
            fail(err)
        }, WS_FIRST_CHUNK_MS)
    }

    return donePromise
}

export async function cancelStream(requestId) {
    return jsonFetch(`${BASE_URL}/api/chat/cancel`, {
        method: 'POST',
        body: JSON.stringify({ request_id: requestId }),
    })
}

/**
 * Маппинг чанков сервера → формат useChatStore.applyChunk.
 * Сервер шлёт text c phase=start/delta/end; store ожидает три разных события text:start/delta/end.
 */
function emitChunks(payload, onChunk) {
    if (!onChunk || !payload) return

    if (payload.type === 'text') {
        switch (payload.phase) {
            case 'start':
                onChunk({ type: 'text:start', mode: payload.mode || 'plain', content: payload.content })
                break
            case 'delta':
                if (typeof payload.delta === 'string') {
                    onChunk({ type: 'text:delta', delta: payload.delta })
                }
                break
            case 'end':
                onChunk({ type: 'text:end', text: payload.text || '', content: payload.content })
                break
            default:
                break
        }
        return
    }

    if (payload.type === 'progress') {
        // Пользователь видит «AI думает / выполняет инструмент» — поднимает доверие к UX.
        // `tool`/`stage` нужны для таймлайна процесса (progressSteps.js): по ним
        // шаги «tool.start»/«tool.end» сопоставляются и получают деталь результата.
        onChunk({
            type: 'progress',
            stage: payload.stage,
            message: payload.message,
            tool: payload.tool,
        })
        return
    }

    // `tool`-чанки (для админ-прозрачности) больше НЕ превращаем в progress:
    // бэкенд шлёт парный progress-чанк (tool.start/tool.end) с человекочитаемым
    // лейблом и деталью результата без технических имён инструментов. Сырой
    // tool-чанк просто пробрасываем дальше (store его игнорирует).

    if (payload.type === 'meta') {
        // Прокидываем request_id из stream-meta — store сохранит для cancel.
        // userMessageId/assistantMessageId — БД-id, нужны для selection mode
        // (share/publish), чтобы выбрать сообщения сразу без перезагрузки страницы.
        onChunk({
            type: 'meta',
            requestId: payload.requestId,
            threadId: payload.threadId,
            userMessageId: payload.userMessageId,
            assistantMessageId: payload.assistantMessageId,
            responseVariants: payload.responseVariants,
        })
        return
    }

    onChunk(payload)
}

export async function getChatPlan() {
    return jsonFetch(`${BASE_URL}/api/chat/plan`)
}

/**
 * Публичный прайс-лист тарифов чата.
 * Бэкенд (ChatBillingController::plans) оборачивает в ApiController::response —
 * поэтому ответ выглядит как { success, data: { plans: [...] } }.
 */
export async function getChatBillingPlans() {
    const response = await jsonFetch(`${BASE_URL}/api/chat/plans`)
    return response?.data?.plans || response?.plans || []
}

export async function getChatRegionsConfigPreview(planCode) {
    const response = await jsonFetch(
        `${BASE_URL}/api/chat/regions/config-preview?plan_code=${encodeURIComponent(planCode)}`,
    )
    return response?.data || response || {}
}

export async function getChatRegionsCurrent() {
    const response = await jsonFetch(`${BASE_URL}/api/chat/regions/current`)
    return response?.data || response || {}
}

/**
 * Справочник регионов источников (публично, для гостей).
 */
export async function getChatRegionsCatalog() {
    const response = await jsonFetch(`${BASE_URL}/api/chat/regions/catalog`)
    return response?.data || response || {}
}

/**
 * Каталог шаблонов ответа (публично, работает и для гостя).
 * Возвращает { layouts: [{ id, title, subtitle, when_to_use, skeleton }], default }.
 */
export async function getChatResponseFormatCatalog() {
    const response = await jsonFetch(`${BASE_URL}/api/chat/response-format/catalog`)
    return response?.data || response || {}
}

/** Сохранённые шаблоны ответа пользователя. Для гостя вернёт ошибку — вызывать под auth. */
export async function getChatResponseFormatCurrent() {
    const response = await jsonFetch(`${BASE_URL}/api/chat/response-format/current`)
    return response?.data || response || {}
}

export async function changeChatResponseFormat(responseFormats) {
    const response = await jsonFetch(`${BASE_URL}/api/chat/response-format/change`, {
        method: 'POST',
        body: JSON.stringify({ response_formats: Array.isArray(responseFormats) ? responseFormats : [] }),
    })
    return response?.data || response || {}
}

export async function changeChatRegions(payload) {
    const returnUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/chat`
        : '/chat'
    const response = await jsonFetch(`${BASE_URL}/api/chat/regions/change`, {
        method: 'POST',
        body: JSON.stringify({
            ...payload,
            return_url: payload?.return_url || returnUrl,
        }),
    })
    const data = response?.data || response || {}
    const paymentUrl = data.payment_url
    if (paymentUrl && typeof window !== 'undefined') {
        window.location.href = paymentUrl
    }
    return data
}

export async function getChatSourcesConfigPreview(planCode) {
    const response = await jsonFetch(
        `${BASE_URL}/api/chat/sources/config-preview?plan_code=${encodeURIComponent(planCode)}`,
    )
    return response?.data || response || {}
}

export async function getChatSourcesCurrent() {
    const response = await jsonFetch(`${BASE_URL}/api/chat/sources/current`)
    return response?.data || response || {}
}

export async function changeChatSources(payload) {
    const returnUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/chat`
        : '/chat'
    const response = await jsonFetch(`${BASE_URL}/api/chat/sources/change`, {
        method: 'POST',
        body: JSON.stringify({
            ...payload,
            return_url: payload?.return_url || returnUrl,
        }),
    })
    const data = response?.data || response || {}
    const paymentUrl = data.payment_url
    if (paymentUrl && typeof window !== 'undefined') {
        window.location.href = paymentUrl
    }
    return data
}

export async function createChatPlanOrder(planCode, billingPeriod = 'month', config = null) {
    const returnUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/chat`
        : '/chat'
    const period = billingPeriod === 'year' ? 'year' : 'month'
    const body = {
        plan_code: planCode,
        return_url: returnUrl,
        billing_period: period,
    }
    if (config) {
        body.config = config
    }
    const response = await jsonFetch(`${BASE_URL}/api/chat/plan-orders`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    const paymentUrl = response?.data?.payment_url || response?.payment_url
    if (paymentUrl && typeof window !== 'undefined') {
        // Ecommerce: пользователь ушёл на страницу оплаты тарифа чата.
        const priceRub = Number(response?.data?.amount_rub
            ?? response?.amount_rub
            ?? response?.data?.amount
            ?? response?.amount
            ?? 0)
        const planName = response?.data?.plan_name || response?.plan_name || planCode
        const periodSuffix = period === 'year' ? 'yearly' : 'monthly'
        const periodLabel = period === 'year' ? 'На год' : 'Помесячно'
        ecommerceAdd({
            id: `tariff_${planCode}_${periodSuffix}`,
            name: `Тариф ${planName} (${periodLabel})`,
            category: 'Подписка/Тарифы',
            price: priceRub,
            quantity: 1,
        })
        window.location.href = paymentUrl
    }
    return response
}


function daysLeftFrom(iso) {
    if (!iso) return null
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    const ms = date.getTime() - Date.now()
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)))
}

export async function getChatLimits() {
    const usage = await jsonFetch(`${BASE_URL}/api/chat/usage`)
    const plan = await getChatPlan().catch(() => null)
    const expiresAt = usage?.expires_at ?? plan?.expires_at ?? null
    return {
        plan: plan?.plan?.code || 'free',
        planName: plan?.plan?.name || '',
        tokensUsed: usage?.tokens?.used ?? 0,
        tokensTotal: usage?.tokens?.total ?? 0,
        requestsUsed: usage?.requests?.used ?? 0,
        requestsTotal: usage?.requests?.total ?? 0,
        allowed: usage?.allowed ?? true,
        reason: usage?.reason ?? null,
        expiresAt,
        isTrial: usage?.is_trial ?? false,
        // /chat/plan может не ответить (см. catch выше) — приоритет у usage.
        isPaid: usage?.is_paid ?? plan?.plan?.is_paid ?? false,
        daysLeft: daysLeftFrom(expiresAt),
        // Фичи тарифа: /chat/usage отдаёт их первым эшелоном (запрос идёт
        // всегда), /chat/plan — как fallback. Если бэк ещё не отдаёт поле
        // (старая версия) — считаем фичу доступной, чтобы не блокировать
        // существующий UI по случайности.
        allowShare: usage?.allow_share ?? plan?.plan?.allow_share ?? true,
        allowExport: usage?.allow_export ?? plan?.plan?.allow_export ?? true,
    }
}

function formatRelativeTime(iso) {
    if (!iso) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    const now = new Date()
    const diffSec = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000))
    if (diffSec < 60) return 'Только что'
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin} мин назад`
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24 && now.getDate() === date.getDate()) return `${diffH} ч назад`
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
    if (
        date.getDate() === yesterday.getDate()
        && date.getMonth() === yesterday.getMonth()
        && date.getFullYear() === yesterday.getFullYear()
    ) {
        return 'Вчера'
    }
    const sameYear = date.getFullYear() === now.getFullYear()
    return date.toLocaleDateString('ru-RU', sameYear
        ? { day: 'numeric', month: 'short' }
        : { day: 'numeric', month: 'short', year: 'numeric' })
}

function buildQuery(params = {}) {
    const q = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue
        q.append(key, String(value))
    }
    const qs = q.toString()
    return qs ? `?${qs}` : ''
}

function mapThreadItem(t) {
    return {
        id: t.id,
        title: t.title || 'Без названия',
        timeLabel: formatRelativeTime(t.last_activity_at || t.created_at),
        active: false,
        pinned: !!t.is_pinned,
        isPublishedReport: !!t.is_published_report,
    }
}

export async function getThreads(params = {}) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/threads${buildQuery({
        page: params.page ?? 1,
        per_page: params.per_page ?? 10,
        search: params.search,
    })}`)
    return {
        items: (data?.items || []).map(mapThreadItem),
        meta: data?.meta || {
            current_page: 1,
            last_page: 1,
            per_page: 10,
            total: 0,
            has_more: false,
        },
    }
}

export async function getThread(threadId) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/threads/${encodeURIComponent(threadId)}`)
    const t = data?.thread
    return t ? mapThreadItem(t) : null
}

export async function getChatHistory(threadId) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/threads/${encodeURIComponent(threadId)}/messages`)
    return {
        threadId,
        thread: data?.thread || null,
        messages: data?.messages || [],
    }
}

export async function patchActiveVariant(threadId, messageId, activeIndex) {
    const data = await jsonFetch(
        `${BASE_URL}/api/chat/threads/${encodeURIComponent(threadId)}/messages/${messageId}/active-variant`,
        {
            method: 'PATCH',
            body: JSON.stringify({ active_index: activeIndex }),
        },
    )
    return data?.message || null
}

export async function getAvailableSources() {
    const data = await jsonFetch(`${BASE_URL}/api/chat/sources/available`)
    return (data?.sources || []).map(s => ({ id: s.code, label: s.name, kind: s.kind, description: s.description }))
}

/**
 * Стартовые подсказки нового диалога (единый источник для обычного чата и embed).
 * Возвращает [{ id, label, prompt }]. Публичный эндпоинт (чат гостевой).
 */
/**
 * Стартовые подсказки: общие для всех + персональные шаблоны пользователя.
 * Персональные несут формат ответа (пресеты и блоки), поэтому возвращаются
 * отдельным списком.
 */
export async function getChatSuggestions() {
    const data = await jsonFetch(`${BASE_URL}/api/chat/suggestions`)
    const clean = items => (items || [])
        .filter(item => item && item.label && item.prompt)
        .map(item => ({
            id: item.id,
            label: String(item.label),
            prompt: String(item.prompt),
            layoutIds: Array.isArray(item.layout_ids) ? item.layout_ids : [],
            blocks: Array.isArray(item.blocks) ? item.blocks : [],
        }))

    return { items: clean(data?.items), personal: clean(data?.personal) }
}

export async function createThread(payload = {}) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/threads`, {
        method: 'POST',
        body: JSON.stringify({
            title: payload.title,
            context_chips: payload.contextChips,
            external_id: payload.id,
        }),
    })
    return data?.thread
}

export async function renameThread(threadId, title) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/threads/${encodeURIComponent(threadId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
    })
    return data?.thread
}

export async function deleteThread(threadId) {
    return jsonFetch(`${BASE_URL}/api/chat/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' })
}

// Маппинг category → икона из набора ChatIcon. Если категория неизвестна — folder.
const REPORT_ICON_BY_CATEGORY = {
    market: 'trending-up',
    forecast: 'trending-up',
    'top-brands': 'columns',
    brands: 'columns',
    regions: 'map',
    credit: 'wallet',
    loans: 'wallet',
    commercial: 'truck',
    ev: 'bolt',
    used: 'rotate',
    dealers: 'store',
}

function reportToListItem(r) {
    const category = (r?.category || '').toLowerCase()
    return {
        id: r?.token,
        token: r?.token,
        icon: REPORT_ICON_BY_CATEGORY[category] || 'folder',
        title: r?.title || 'Без названия',
        subtitle: r?.description || (r?.category ? `${r.category}` : 'Опубликован'),
        category: r?.category || null,
        updatedAt: r?.updated_at || null,
        viewCount: typeof r?.view_count === 'number' ? r.view_count : 0,
    }
}

/**
 * Список «Готовых отчётов» для сайдбара (read-only, доступен и гостю).
 */
export async function getReports() {
    const data = await jsonFetch(`${BASE_URL}/api/chat/reports/public`).catch(() => null)
    const items = data?.items || []
    return items.map(reportToListItem)
}

/**
 * Открыть конкретный публичный отчёт по token — возвращает мета + сообщения.
 */
export async function getPublicReport(token) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/reports/public/${encodeURIComponent(token)}`)
    return {
        token: data?.token,
        title: data?.title,
        description: data?.description,
        category: data?.category,
        messages: data?.messages || [],
        updatedAt: data?.updated_at || null,
        viewCount: typeof data?.view_count === 'number' ? data.view_count : 0,
    }
}

/**
 * Форкнуть публичный отчёт в свой thread (требует авторизацию).
 * Возвращает external_id нового треда, в который можно сразу редиректнуть.
 */
export async function forkPublicReport(token) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/reports/public/${encodeURIComponent(token)}/fork`, {
        method: 'POST',
        body: JSON.stringify({}),
    })
    return data?.thread_id || null
}

/**
 * Создать share-ссылку на выбранные сообщения треда.
 *
 * mode = 'forkable' (по умолчанию) разрешает получателю клонировать ветку в свой чат,
 * 'read_only' — только просмотр. expiresAt — ISO-строка или null (без срока).
 */
export async function createShare({ threadExternalId, messageIds, mode = 'forkable', title = null, expiresAt = null }) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/shares`, {
        method: 'POST',
        body: JSON.stringify({
            thread_external_id: threadExternalId,
            message_ids: messageIds,
            mode,
            title,
            expires_at: expiresAt,
        }),
    })
    return {
        id: data?.id,
        token: data?.token,
        mode: data?.mode,
        title: data?.title,
        expiresAt: data?.expires_at,
    }
}

export async function getMyShares() {
    const data = await jsonFetch(`${BASE_URL}/api/chat/shares`).catch(() => null)
    const items = data?.items || []
    return items.map(s => ({
        id: s.id,
        token: s.token,
        title: s.title || 'Без названия',
        mode: s.mode,
        isActive: Boolean(s.is_active),
        expiresAt: s.expires_at || null,
        revokedAt: s.revoked_at || null,
        viewCount: typeof s.view_count === 'number' ? s.view_count : 0,
        createdAt: s.created_at || null,
    }))
}

export async function revokeShare(id) {
    return jsonFetch(`${BASE_URL}/api/chat/shares/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/**
 * Публичный просмотр share-ссылки (без auth — гость тоже может прочитать).
 */
export async function getShareByToken(token) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/shares/${encodeURIComponent(token)}`)
    return {
        mode: data?.mode,
        messages: data?.messages || [],
        thread: data?.thread || null,
        owner: data?.owner || null,
    }
}

export async function forkShare(token) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/shares/${encodeURIComponent(token)}/fork`, {
        method: 'POST',
        body: JSON.stringify({}),
    })
    return data?.thread_id || null
}

/**
 * Админская публикация: создать готовый отчёт из выбранных сообщений своего треда.
 * Бэкенд сразу ставит is_published=true.
 */
/**
 * Отправить feedback (полезно / бесполезно) на конкретное сообщение.
 * Fire-and-forget: если бэкенд ещё не реализовал эндпоинт — ошибка проглатывается.
 *
 * @param {string} threadId  — external_id треда
 * @param {number} messageId — serverId сообщения (БД)
 * @param {'up'|'down'|null} value — оценка (null = сброс)
 */
export async function sendFeedback(threadId, messageId, value) {
    return jsonFetch(
        `${BASE_URL}/api/chat/messages/${messageId}/feedback`,
        {
            method: 'POST',
            body: JSON.stringify({ rating: value }),
        },
    )
}

/* -------------------------------------------------------------------------- */
/*  Мои файлы (PDF-экспорты диалогов).                                        */
/* -------------------------------------------------------------------------- */

/**
 * Список PDF-экспортов текущего пользователя (для модалки «Мои файлы»).
 * Работает только под auth — для гостя сервер вернёт 401 и мы отдадим [].
 */
export async function getMyExports() {
    const data = await jsonFetch(`${BASE_URL}/api/chat/exports`).catch(() => null)
    const items = data?.items || []
    return items.map(x => ({
        id: x.id,
        filename: x.filename,
        title: x.title || x.filename,
        sizeBytes: Number(x.size_bytes || 0),
        threadId: x.thread_id || null,
        createdAt: x.created_at || null,
    }))
}

/**
 * Загрузить готовый PDF (Blob, сгенерированный html2pdf.js) в S3 и создать
 * запись `chat_exports`. multipart/form-data — потому что jsonFetch не годится:
 * тут нельзя ставить Content-Type=application/json.
 */
export async function createExport({ threadExternalId, blob, filename, title = null, messageIds = [] }) {
    // Ранние guard'ы: без них Laravel ответит невнятным «field is required»,
    // хотя причина в клиентском коде — проще заметить сразу в консоли.
    if (!threadExternalId) throw new Error('createExport: threadExternalId is required')
    if (!blob) throw new Error('createExport: blob is required')
    if (!filename) throw new Error('createExport: filename is required')

    const form = new FormData()
    form.append('thread_external_id', threadExternalId)
    form.append('file', blob, filename)
    form.append('filename', filename)
    if (title) form.append('title', title)
    for (const id of messageIds) form.append('message_ids[]', String(id))

    // ВАЖНО: для multipart/form-data НЕЛЬЗЯ вручную ставить Content-Type —
    // браузер сам подставит `multipart/form-data; boundary=…`. Если пройти
    // через общий authHeaders(), туда прилетит `application/json`, Laravel
    // не распарсит тело и валидация упадёт как «field is required» на все
    // обязательные поля разом. Поэтому здесь собираем заголовки руками —
    // Authorization/Accept/session-token — без Content-Type.
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
    const sessionToken = getSessionToken()
    const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(sessionToken ? { 'X-Chat-Session-Token': sessionToken } : {}),
    }
    const response = await fetch(`${BASE_URL}/api/chat/exports`, {
        method: 'POST',
        headers,
        body: form,
    })
    const issued = response.headers.get('X-Chat-Session-Token')
    if (issued) storeSessionToken(issued)
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
        const error = new Error(data?.message || `Request failed: ${response.status}`)
        error.status = response.status
        error.payload = data
        throw error
    }
    return {
        id: data?.id,
        filename: data?.filename,
        title: data?.title,
        sizeBytes: Number(data?.size_bytes || 0),
        createdAt: data?.created_at || null,
    }
}

/**
 * Presigned URL для скачивания. Оставлен на случай отладки/будущего внешнего
 * шаринга — UI использует downloadExport() ниже, потому что presigned URL
 * MinIO в docker-сети смотрит на внутренний хост, недоступный из браузера.
 */
export async function getExportUrl(id) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/exports/${encodeURIComponent(id)}/url`)
    return {
        url: data?.url || null,
        filename: data?.filename || null,
    }
}

/**
 * Прокси-скачивание PDF через наш бэк. Возвращает { blob, filename }; UI
 * сам вызовет saveAs — так работает и без публичного адреса MinIO/S3, и
 * контроль доступа гарантированно проходит через auth:api.
 *
 * Filename извлекаем из Content-Disposition:
 *   `attachment; filename="ascii.pdf"; filename*=UTF-8''<encoded>`.
 * Приоритет отдаём filename* (RFC 5987) — там честный UTF-8, если сервер
 * прислал кириллицу.
 */
export async function downloadExport(id) {
    // Ручная сборка headers, потому что fetch в этом случае обычный (без
    // multipart), но всё равно тянуть authHeaders() с Content-Type=json
    // необязательно и слегка «шумно» — тут нужен только Bearer/session.
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
    const sessionToken = getSessionToken()
    const headers = {
        Accept: 'application/pdf',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(sessionToken ? { 'X-Chat-Session-Token': sessionToken } : {}),
    }
    const response = await fetch(`${BASE_URL}/api/chat/exports/${encodeURIComponent(id)}/download`, {
        method: 'GET',
        headers,
    })
    if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        const error = new Error(data?.message || `Download failed: ${response.status}`)
        error.status = response.status
        error.payload = data
        throw error
    }

    // Читаем filename из Content-Disposition. Пример:
    //   attachment; filename="chat_thread.pdf"; filename*=UTF-8''chat_thread.pdf
    let filename = null
    const cd = response.headers.get('Content-Disposition') || ''
    const starMatch = cd.match(/filename\*=UTF-8''([^;]+)/i)
    if (starMatch) {
        try { filename = decodeURIComponent(starMatch[1]) } catch { filename = null }
    }
    if (!filename) {
        const m = cd.match(/filename="([^"]+)"/i)
        if (m) filename = m[1]
    }

    const blob = await response.blob()
    return { blob, filename }
}

export async function deleteExport(id) {
    return jsonFetch(`${BASE_URL}/api/chat/exports/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function publishPublicReport({ threadExternalId, messageIds, title, description, category }) {
    const data = await jsonFetch(`${BASE_URL}/api/chat/reports/publish`, {
        method: 'POST',
        body: JSON.stringify({
            thread_external_id: threadExternalId,
            message_ids: messageIds,
            title,
            description,
            category,
        }),
    })
    return data?.token || null
}
